import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, goalsApi, seedApi, performanceCycleApi, settingsApi, knowledgeBaseApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Target,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  Loader2,
  Award,
  Quote,
  BookOpen,
  ExternalLink,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, description, variant = 'default' }) => (
  <Card className="card-hover">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className={`h-5 w-5 ${variant === 'primary' ? 'text-primary' : 'text-muted-foreground'}`} />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </CardContent>
  </Card>
);

const CycleTimeline = ({ currentStage, performanceCycle }) => {
  // Helper function to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '';
    }
  };

  const stages = [
    { 
      key: 'goal_setting', 
      label: 'Goal Setting', 
      date: formatDate(performanceCycle?.goal_setting_start) || 'Jan 1' 
    },
    { 
      key: 'mid_year', 
      label: 'Mid-Year Review', 
      date: formatDate(performanceCycle?.mid_year_start) || 'Jul 1' 
    },
    { 
      key: 'year_end', 
      label: 'Year-End Review', 
      date: formatDate(performanceCycle?.year_end_start) || 'Jan 1' 
    },
    { 
      key: 'calibration', 
      label: 'Calibration', 
      date: formatDate(performanceCycle?.calibration_start) || 'Feb 1' 
    },
  ];

  // Find the index of the current stage
  const currentStageIndex = stages.findIndex(s => s.key === currentStage);

  // Determine stage status: 'passed', 'current', or 'upcoming'
  const getStageStatus = (index) => {
    if (index < currentStageIndex) return 'passed';
    if (index === currentStageIndex) return 'current';
    return 'upcoming';
  };

  // Get color classes based on status
  const getStageColors = (status) => {
    switch (status) {
      case 'passed':
        return 'bg-green-500 text-white';
      case 'current':
        return 'bg-primary text-primary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Get connector line color
  const getLineColor = (index) => {
    if (index < currentStageIndex) return 'bg-green-500';
    if (index === currentStageIndex) return 'bg-primary';
    return 'bg-muted';
  };

  return (
    <div className="flex items-center justify-between">
      {stages.map((stage, index) => {
        const status = getStageStatus(index);
        return (
          <div key={stage.key} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${getStageColors(status)}`}
              >
                {status === 'passed' ? '✓' : index + 1}
              </div>
            <p className="text-xs font-medium mt-2">{stage.label}</p>
            <p className="text-xs text-muted-foreground">{stage.date}</p>
          </div>
          {index < stages.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 ${getLineColor(index)}`} />
          )}
        </div>
        );
      })}
    </div>
  );
};

export default function DashboardPage() {
  const { user, isAdmin, isHR, isManager } = useAuth();
  const [stats, setStats] = useState(null);
  const [cycleStatus, setCycleStatus] = useState(null);
  const [performanceCycle, setPerformanceCycle] = useState(null);
  const [orgSettings, setOrgSettings] = useState(null);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, cycleRes, perfCycleRes, settingsRes, kbRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getCycleStatus(),
        performanceCycleApi.get(currentYear),
        settingsApi.get(),
        knowledgeBaseApi.getAll(),
      ]);
      setStats(statsRes.data);
      setCycleStatus(cycleRes.data);
      setPerformanceCycle(perfCycleRes.data);
      setOrgSettings(settingsRes.data);
      setKnowledgeBase(kbRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await seedApi.seed();
      toast.success(response.data.message);
      loadDashboardData();
    } catch (error) {
      console.error('Seed failed:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.name}! Here's your performance overview.
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          <Calendar className="h-4 w-4 mr-1" />
          {cycleStatus?.year || new Date().getFullYear()}
        </Badge>
      </div>

      {/* Performance Cycle Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Performance Cycle</CardTitle>
          <CardDescription>Current stage: {cycleStatus?.stage_name}</CardDescription>
        </CardHeader>
        <CardContent>
          <CycleTimeline currentStage={cycleStatus?.current_stage} performanceCycle={performanceCycle} />
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {(isAdmin || isHR) && (
          <>
            <StatCard
              title="Total Employees"
              value={stats?.total_employees || 0}
              icon={Users}
              description="Active employees"
            />
            <StatCard
              title="Total Managers"
              value={stats?.total_managers || 0}
              icon={Users}
              description="Team leads"
            />
            <StatCard
              title="Pending Approvals"
              value={stats?.goals_pending_approval || 0}
              icon={Clock}
              description="Goals awaiting review"
              variant="primary"
            />
            <StatCard
              title="Reviews Completed"
              value={stats?.reviews_completed || 0}
              icon={CheckCircle}
              description="This cycle"
            />
          </>
        )}

        {isManager && !isAdmin && !isHR && (
          <>
            <StatCard
              title="Total Team"
              value={stats?.team_size || 0}
              icon={Users}
              description="Direct reports"
            />
            <StatCard
              title="Goal Setting Completed"
              value={stats?.team_goals_completed || 0}
              icon={Target}
              description="Team members with approved goals"
            />
            <StatCard
              title="Mid-Year Review Completed"
              value={stats?.team_midyear_completed || 0}
              icon={CheckCircle}
              description="Mid-year reviews done"
            />
            <StatCard
              title="Year-End Review Completed"
              value={stats?.team_yearend_completed || 0}
              icon={Award}
              description="Year-end reviews done"
            />
            <StatCard
              title="Pending Manager Items"
              value={(stats?.goals_pending_approval || 0) + (stats?.team_reviews_pending || 0)}
              icon={Clock}
              description="Awaiting your action"
              variant="primary"
            />
          </>
        )}

        {!isAdmin && !isHR && !isManager && (
          <>
            <StatCard
              title="My Goals"
              value={stats?.my_goals_count || 0}
              icon={Target}
              description="This year"
            />
            <StatCard
              title="Approved Goals"
              value={stats?.approved_goals || 0}
              icon={CheckCircle}
              description="Ready for tracking"
            />
            <StatCard
              title="Pending Approval"
              value={stats?.pending_goals || 0}
              icon={Clock}
              description="Awaiting manager review"
            />
            <StatCard
              title="Pending On You"
              value={stats?.pending_on_employee || 0}
              icon={AlertCircle}
              description="Stages awaiting your submission"
              variant="primary"
            />
            <StatCard
              title="Performance Year"
              value={cycleStatus?.year || new Date().getFullYear()}
              icon={Calendar}
              description="Current cycle"
            />
          </>
        )}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
          <CardDescription>Common tasks based on your role</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(isAdmin || isHR) && (
              <>
                <Button variant="outline" onClick={() => window.location.href = '/users'} data-testid="manage-users-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Manage Users
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/reports'} data-testid="view-reports-btn">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  View Reports
                </Button>
              </>
            )}
            {isManager && (
              <Button variant="outline" onClick={() => window.location.href = '/team-goals'} data-testid="review-team-goals-btn">
                <Target className="h-4 w-4 mr-2" />
                Review Team Goals
              </Button>
            )}
            <Button onClick={() => window.location.href = '/goals'} data-testid="my-goals-btn">
              <Target className="h-4 w-4 mr-2" />
              My Goals
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Knowledge Base */}
      {knowledgeBase.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Knowledge Base
            </CardTitle>
            <CardDescription>Helpful resources and documentation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {knowledgeBase.map((item) => (
                <a
                  key={item.id}
                  href={item.drive_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50 hover:bg-slate-100 hover:border-primary/50 transition-colors group"
                  data-testid={`kb-item-${item.id}`}
                >
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700 group-hover:text-primary">
                    {item.title}
                  </span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMART Objectives Reference */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">SMART Objectives (KPI's) - Goal Setting</CardTitle>
          <CardDescription>Guidelines for setting effective performance objectives</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {[
              { 
                letter: 'S', 
                label: 'Specific', 
                color: 'bg-blue-600',
                borderColor: 'border-blue-600',
                bgLight: 'bg-blue-50',
                description: 'Objectives should be straightforward and emphasize the business result that you would like to achieve.'
              },
              { 
                letter: 'M', 
                label: 'Measurable', 
                color: 'bg-green-600',
                borderColor: 'border-green-600',
                bgLight: 'bg-green-50',
                description: 'Establish concrete criteria for measuring progress toward the attainment of each objective that you set.'
              },
              { 
                letter: 'A', 
                label: 'Achievable', 
                color: 'bg-amber-500',
                borderColor: 'border-amber-500',
                bgLight: 'bg-amber-50',
                description: 'Objectives must be within your capacity to reach. You cannot commit to accomplishing objectives that are far too out of your reach, or unreasonable.'
              },
              { 
                letter: 'R', 
                label: 'Relevant', 
                color: 'bg-purple-600',
                borderColor: 'border-purple-600',
                bgLight: 'bg-purple-50',
                description: 'Make sure each objective is linked with the department KPIs and fits with your immediate and long-range plans.'
              },
              { 
                letter: 'T', 
                label: 'Time Bound', 
                color: 'bg-red-500',
                borderColor: 'border-red-500',
                bgLight: 'bg-red-50',
                description: 'Setting an end point on your objectives gives you a clear target to work towards.'
              },
            ].map((item) => (
              <div key={item.letter} className={`rounded-lg border-2 ${item.borderColor} ${item.bgLight} p-3 text-center`}>
                <div className={`w-10 h-10 rounded-full ${item.color} flex items-center justify-center text-white font-bold text-lg mx-auto mb-2`}>
                  {item.letter}
                </div>
                <h4 className="font-semibold text-sm mb-2">{item.label}</h4>
                <p className="text-xs text-muted-foreground leading-tight">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rating Scale Reference */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Rating Scale Reference</CardTitle>
          <CardDescription>Performance rating definitions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {[
              { 
                score: 5, 
                label: 'Outstanding', 
                range: '4.6 - 5.0', 
                color: 'bg-teal-500',
                borderColor: 'border-teal-500',
                bgLight: 'bg-teal-50',
                target: '105% and above',
                description: 'Significantly beyond target. Exceptional contributions.'
              },
              { 
                score: 4, 
                label: 'Exceeds Expectation', 
                range: '3.8 - 4.5', 
                color: 'bg-green-500',
                borderColor: 'border-green-500',
                bgLight: 'bg-green-50',
                target: '101% - 104%',
                description: 'Met and sometimes exceeded targets. All expectations fulfilled.'
              },
              { 
                score: 3, 
                label: 'Meets Expectation', 
                range: '2.7 - 3.7', 
                color: 'bg-blue-500',
                borderColor: 'border-blue-500',
                bgLight: 'bg-blue-50',
                target: '90% - 100%',
                description: 'Achieved target levels. All job requirements fulfilled.'
              },
              { 
                score: 2, 
                label: 'Partially Meets', 
                range: '1.7 - 2.6', 
                color: 'bg-yellow-500',
                borderColor: 'border-yellow-500',
                bgLight: 'bg-yellow-50',
                target: '85% - 89%',
                description: 'Some targets not met. Improvement required.'
              },
              { 
                score: 1, 
                label: 'Does Not Meet', 
                range: '1.0 - 1.6', 
                color: 'bg-red-500',
                borderColor: 'border-red-500',
                bgLight: 'bg-red-50',
                target: '84% and below',
                description: 'Few objectives met. Performance improvement must be achieved.'
              },
            ].map((rating) => (
              <div key={rating.score} className={`rounded-lg border-2 ${rating.borderColor} ${rating.bgLight} p-3 text-center`}>
                <div className={`w-10 h-10 rounded-full ${rating.color} flex items-center justify-center text-white font-bold text-lg mx-auto mb-2`}>
                  {rating.score}
                </div>
                <h4 className="font-semibold text-sm mb-1">{rating.label}</h4>
                <p className="text-xs text-muted-foreground mb-2">({rating.range})</p>
                <div className="border-t pt-2 mt-2">
                  <p className="text-xs font-medium text-blue-600 mb-1">Target: {rating.target}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{rating.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CEO Message Section */}
      {orgSettings?.ceo_message && orgSettings?.ceo_image_url && (
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              {/* CEO Image */}
              <div className="flex-shrink-0">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg">
                  <img
                    src={orgSettings.ceo_image_url}
                    alt={orgSettings.ceo_name || 'CEO'}
                    className="w-full h-full object-cover"
                    data-testid="ceo-image"
                  />
                </div>
              </div>
              
              {/* Message Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 mb-3">
                  <p className="text-base text-slate-700 italic leading-relaxed" data-testid="ceo-message">
                    "{orgSettings.ceo_message}"
                  </p>
                </div>
                <div className="text-right" data-testid="ceo-attribution">
                  {orgSettings.ceo_name && (
                    <p className="text-sm font-semibold text-slate-700">
                      — {orgSettings.ceo_name}
                    </p>
                  )}
                  {orgSettings.ceo_title && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {orgSettings.ceo_title}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
